import { format, minify, validate, type ValidationResult } from '../core/json';
import { createEditor, type EditorHandle } from '../editor/editor';
import { el, button, downloadText, copyText } from './dom';

const SAMPLE = `{
  "name": "json-viewer",
  "private": true,
  "features": ["format", "diff", "validate"],
  "indent": 2,
  "nested": { "a": 1, "b": [true, false, null] }
}`;

type IndentChoice = '2' | '4' | 'tab';

function indentValue(choice: IndentChoice): number | string {
  return choice === 'tab' ? '\t' : Number(choice);
}

/** Format / validate tab. Owns its editor and status bar. */
export function createFormatView(): HTMLElement {
  let indent: IndentChoice = '2';
  let debounce: number | undefined;

  const status = el('div', { class: 'status', role: 'status', 'aria-live': 'polite' });
  const editorHost = el('div', { class: 'editor-host' });

  const editor: EditorHandle = createEditor({
    parent: editorHost,
    placeholder: 'Paste or type JSON here…',
    onChange: () => scheduleStatus(),
  });

  function scheduleStatus(): void {
    if (debounce !== undefined) window.clearTimeout(debounce);
    debounce = window.setTimeout(renderStatus, 250);
  }

  function renderStatus(): void {
    const text = editor.getValue();
    status.replaceChildren();
    if (text.trim() === '') {
      status.dataset.state = 'idle';
      status.append(el('span', {}, ['Ready. Your JSON stays in this browser.']));
      return;
    }
    const result: ValidationResult = validate(text);
    if (!result.ok && result.error) {
      status.dataset.state = 'error';
      const { line, column } = result.error.pos;
      status.append(
        el('span', { class: 'badge badge-error' }, ['Invalid']),
        el('span', {}, [`Line ${line}, Col ${column}: ${result.error.message}`]),
      );
      return;
    }
    status.dataset.state = result.warnings.length > 0 ? 'warn' : 'ok';
    status.append(
      el('span', { class: 'badge badge-ok' }, ['Valid']),
    );
    if (result.warnings.length > 0) {
      status.append(
        el('span', { class: 'badge badge-warn' }, [
          `${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''}`,
        ]),
        el('span', {}, [result.warnings[0].message]),
      );
    }
  }

  function applyTransform(fn: (t: string) => string): void {
    const text = editor.getValue();
    if (text.trim() === '') return;
    try {
      editor.setValue(fn(text));
    } catch {
      // Invalid JSON: leave the input untouched; the status bar/linter already
      // explain what is wrong.
      renderStatus();
    }
  }

  const formatBtn = button('Format', { title: 'Pretty-print (Beautify)' });
  formatBtn.addEventListener('click', () =>
    applyTransform((t) => format(t, { indent: indentValue(indent) })),
  );

  const minifyBtn = button('Minify');
  minifyBtn.addEventListener('click', () => applyTransform((t) => minify(t)));

  const indentSelect = el('select', { class: 'select', 'aria-label': 'Indent size' }, [
    el('option', { value: '2' }, ['2 spaces']),
    el('option', { value: '4' }, ['4 spaces']),
    el('option', { value: 'tab' }, ['Tabs']),
  ]) as HTMLSelectElement;
  indentSelect.value = indent;
  indentSelect.addEventListener('change', () => {
    indent = indentSelect.value as IndentChoice;
  });

  const copyBtn = button('Copy');
  copyBtn.addEventListener('click', async () => {
    const ok = await copyText(editor.getValue());
    copyBtn.textContent = ok ? 'Copied!' : 'Copy failed';
    window.setTimeout(() => (copyBtn.textContent = 'Copy'), 1200);
  });

  const downloadBtn = button('Download');
  downloadBtn.addEventListener('click', () => {
    const text = editor.getValue();
    if (text.trim() !== '') downloadText('data.json', text);
  });

  const sampleBtn = button('Load sample');
  sampleBtn.addEventListener('click', () => {
    editor.setValue(SAMPLE);
    renderStatus();
  });

  const clearBtn = button('Clear');
  clearBtn.addEventListener('click', () => {
    editor.setValue('');
    renderStatus();
  });

  const toolbar = el('div', { class: 'toolbar' }, [
    formatBtn,
    minifyBtn,
    indentSelect,
    el('span', { class: 'spacer' }),
    sampleBtn,
    copyBtn,
    downloadBtn,
    clearBtn,
  ]);

  renderStatus();
  return el('section', { class: 'panel' }, [toolbar, editorHost, status]);
}
